// services/api.js — API barrel.
//
// Historically this was a single ~1,560-line module holding the axios client
// plus every domain's API namespace. It is being split into a shared client
// (services/apiClient.js) and per-domain modules (services/<domain>/api.js).
// This file now RE-EXPORTS everything so the ~90 files that
// `import { fooAPI } from '../services/api'` keep working unchanged — the split
// is additive and behaviour-preserving. Namespaces not yet moved to a domain
// module are still defined inline below; they'll migrate in later passes.
//
// Split so far: services/apiClient.js (client + isMongoId), masterData/, pricing/,
// email/, stock/.

import api, { isMongoId } from './apiClient';

// Re-export the shared client bits so `import api, { isMongoId } from './api'`
// (and the default import) resolve exactly as before.
export { isMongoId };
export default api;

// ── Extracted domain modules (re-exported for back-compat) ─────────────────
export {
  currencyAPI, brandAPI, marketAPI, axisAPI, supplierAPI, brandGroupAPI,
  exchangeRateAPI, segmentAPI, contractorAPI, seasonalCalendarAPI,
} from './masterData/api';
export {
  priceStructureAPI, calculatorAPI, templateAPI, supplierReportTemplateAPI,
  pricingChannelAPI, productLineAPI, brandListMrpApi, priceListAPI,
  getPricingHistory, uploadPriceUpdates,
} from './pricing/api';
export { emailTemplateAPI } from './email/api';
export { shopifyStockAPI, inventreePartsAPI } from './stock/api';

// ── Still-inline namespaces (not yet split) ────────────────────────────────

// Export/Import API
export const exportImportAPI = {
  // Price Structure exports
  exportPriceStructure: (id) => ({
    method: 'GET',
    url: `/export/price-structure/${id}`,
    responseType: 'blob'
  }),
  downloadBulkTemplate: () => ({
    method: 'GET',
    url: `/template/price-structures`,
    responseType: 'blob'
  }),
  bulkUploadPriceStructures: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/price-structures', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Entity templates
  downloadEntityTemplate: (entityType) => api.get(`/template/${entityType}`, { responseType: 'blob' }),

  // Entity exports/imports
  exportEntity: (entityType) => ({
    method: 'GET',
    url: `/export/${entityType}`,
    responseType: 'blob'
  }),
  importEntity: (entityType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/${entityType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Budget API
export const budgetAPI = {
  getAll: () => api.get('/budgets'),
  getById: (id) => api.get(`/budgets/${id}`),
  getByBrand: (brandId) => api.get(`/budgets/brand/${brandId}`),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
  calculate: (data) => api.post('/budgets/calculate', data),
  recalculateForStartMonth: (id, startMonthYear) => api.post(`/budgets/${id}/recalculate-for-start-month`, { startMonthYear }),
  pullFromBP: (data) => api.post('/budgets/pull-from-bp', data),
  getBPValuesForYear: (data) => api.post('/budgets/bp-values-for-year', data),
  getPurchaseValuesForBP: (data) => api.post('/budgets/purchase-values-for-bp', data),
  pushFromEbbp: (data) => api.post('/budgets/upsert-from-ebbp', data),
};

// Business Plan API
export const businessPlanAPI = {
  // Existing CRUD methods
  getAll: (params) => api.get('/business-plans', { params }),
  getById: (id) => api.get(`/business-plans/${id}`),
  create: (data) => api.post('/business-plans', data),
  duplicate: (id, data) => api.post(`/business-plans/${id}/duplicate`, data),
  update: (id, data) => api.put(`/business-plans/${id}`, data),
  // Quick status patch used by the list view's inline editor. The full
  // update endpoint already accepts a partial body, so we just narrow to
  // { status } and skip rebuilding the heavy axes payload from the row.
  // `extra` forwards optional fields that ride along with a status change —
  // e.g. { submittedFollowUpDays } chosen in the submit-to-brand dialog.
  updateStatus: (id, status, extra) => api.put(`/business-plans/${id}`, { status, ...(extra || {}) }),
  delete: (id) => api.delete(`/business-plans/${id}`),
  // Archive (soft-hide from the list) and restore. Pass { archived: 'true' }
  // to getAll to list archived plans instead of active ones.
  archive: (id) => api.patch(`/business-plans/${id}/archive`),
  unarchive: (id) => api.patch(`/business-plans/${id}/unarchive`),

  // Follow-up dashboard. `updateFollowUp` patches only the follow-up fields
  // (owner/date/action/priority/notes) via the lightweight endpoint that skips
  // the heavy axes save hooks. Pass { markFollowedUp: true, historyNote } to
  // also stamp lastFollowUpAt and append a dated followUpHistory entry.
  updateFollowUp: (id, data) => api.patch(`/business-plans/${id}/follow-up`, data),
  // Active-user list for the follow-up owner picker (works for the regular
  // `user` role, unlike the admin-only /api/users endpoint).
  getAssignableUsers: () => api.get('/business-plans/assignable-users'),
  // Server-computed follow-up + pipeline roll-up (optional; the list response
  // already carries the same `metrics` block for the initial render).
  getFollowUpMetrics: (params) => api.get('/business-plans/follow-up/metrics', { params }),

  // Axis management methods
  addAxis: (planId, axisData) => api.post(`/business-plans/${planId}/axes`, axisData),
  updateAxis: (planId, axisIndex, axisData) => api.put(`/business-plans/${planId}/axes/${axisIndex}`, axisData),
  deleteAxis: (planId, axisIndex) => api.delete(`/business-plans/${planId}/axes/${axisIndex}`),

  // Duplicate axis helper
  duplicateAxis: (planId, sourceAxisIndex, newAxisName) =>
    api.post(`/business-plans/${planId}/axes`, {
      copyFromAxisIndex: sourceAxisIndex,
      axisName: newAxisName
    }),

  // Get doors from an existing plan for copying
  getDoorsFromPlan: (planId, axisIndex) => api.get(`/business-plans/${planId}/doors/${axisIndex}`),

  // A&P (Advertising & Promotion) methods
  getAPData: (planId) => api.get(`/business-plans/${planId}/ap`),
  initializeAP: (planId, data = {}) => api.post(`/business-plans/${planId}/ap/initialize`, data),
  updateAPLineItem: (planId, categoryCode, data) =>
    api.put(`/business-plans/${planId}/ap/line-item/${categoryCode}`, data),
  updateAPPercentage: (planId, apPercentage) =>
    api.put(`/business-plans/${planId}/ap/percentage`, { apPercentage }),
  bulkUpdateAPLineItems: (planId, updates) =>
    api.put(`/business-plans/${planId}/ap/bulk-update`, { updates }),
  recalculateAPSummary: (planId) =>
    api.post(`/business-plans/${planId}/ap/recalculate`),
  resetAPData: (planId) => api.post(`/business-plans/${planId}/ap/reset`),
  getAPDataByBrand: (brandId, excludePlanId = null) =>
    api.get(`/business-plans/ap-data/brand/${brandId}`, {
      params: excludePlanId ? { excludePlanId } : {}
    })
};

// Existing Brand Business Plan API — the new-brand BP's sibling for existing
// brands BIB already distributes. pullActuals seeds the plan from historical
// DoorSales / SOB data; CRUD persists the plan (projections + snapshot).
export const existingBrandBusinessPlanAPI = {
  getAll: (params) => api.get('/existing-brand-business-plans', { params }),
  getById: (id) => api.get(`/existing-brand-business-plans/${id}`),
  create: (data) => api.post('/existing-brand-business-plans', data),
  update: (id, data) => api.put(`/existing-brand-business-plans/${id}`, data),
  delete: (id) => api.delete(`/existing-brand-business-plans/${id}`),
  // Pull historical actuals (Sell-In/Sell-Out by door & channel, SOB by door).
  // params: { brand, year, displayCurrency: 'BC' | 'BDT' }
  pullActuals: (params) => api.get('/existing-brand-business-plans/pull-actuals', { params })
};

// Product Line Extrapolator — reference data for the launch plan (product lines
// and their SKUs from InvenTree, retailer share of business from Door Sales,
// month weighting from the Bangladesh retail calendar) plus the Excel export.
export const productLineExtrapolatorAPI = {
  getProductLines: (brandId) => api.get('/product-line-extrapolator/product-lines', { params: { brandId } }),
  getProducts: (brandId, productLines) =>
    api.get('/product-line-extrapolator/products', { params: { brandId, productLines } }),
  getYears: (brandId) => api.get('/product-line-extrapolator/years', { params: { brandId } }),
  getDoorGroups: () => api.get('/product-line-extrapolator/door-groups'),
  getRetailers: (params) => api.get('/product-line-extrapolator/retailers', { params, timeout: 120000 }),
  getFestiveWeights: (params) => api.get('/product-line-extrapolator/festive-weights', { params }),
  exportPlan: (payload) => api.post('/product-line-extrapolator/export', payload, { responseType: 'blob' }),
  // Saved plans
  listPlans: (params) => api.get('/product-line-extrapolator/plans', { params }),
  getPlan: (id) => api.get(`/product-line-extrapolator/plans/${id}`),
  createPlan: (payload) => api.post('/product-line-extrapolator/plans', payload),
  updatePlan: (id, payload) => api.put(`/product-line-extrapolator/plans/${id}`, payload),
  deletePlan: (id) => api.delete(`/product-line-extrapolator/plans/${id}`)
};

// A&P Category API
export const apCategoryAPI = {
  getAll: (includeInactive = false) => api.get('/ap-categories', { params: { includeInactive } }),
  getTree: () => api.get('/ap-categories/tree'),
  getStats: () => api.get('/ap-categories/stats'),
  getById: (id) => api.get(`/ap-categories/${id}`),
  getByCode: (code) => api.get(`/ap-categories/code/${code}`),
  create: (data) => api.post('/ap-categories', data),
  update: (id, data) => api.put(`/ap-categories/${id}`, data),
  delete: (id) => api.delete(`/ap-categories/${id}`),
  reorder: (orders) => api.put('/ap-categories/reorder', { orders }),
  resetToDefaults: () => api.post('/ap-categories/reset')
};

// Brand A&P Defaults API
export const brandAPDefaultsAPI = {
  getByBrand: (brandId) => api.get(`/brand-ap-defaults/brand/${brandId}`),
  upsert: (brandId, data) => api.put(`/brand-ap-defaults/brand/${brandId}`, data),
  delete: (brandId) => api.delete(`/brand-ap-defaults/brand/${brandId}`),
  updateCategoryOverride: (brandId, categoryCode, data) =>
    api.put(`/brand-ap-defaults/brand/${brandId}/category/${categoryCode}`, data),
  removeCategoryOverride: (brandId, categoryCode) =>
    api.delete(`/brand-ap-defaults/brand/${brandId}/category/${categoryCode}`),
  addCustomCategory: (brandId, data) =>
    api.post(`/brand-ap-defaults/brand/${brandId}/custom-category`, data),
  removeCustomCategory: (brandId, categoryCode) =>
    api.delete(`/brand-ap-defaults/brand/${brandId}/custom-category/${categoryCode}`)
};

// Door API
export const doorAPI = {
  getAll: (params) => api.get('/doors', { params }),
  getById: (id) => api.get(`/doors/${id}`),
  getByChannelFilters: (filters, isActive = true) =>
    api.post('/doors/by-channel-filters', { filters, isActive }),
  create: (data) => api.post('/doors', data),
  update: (id, data) => api.put(`/doors/${id}`, data),
  delete: (id) => api.delete(`/doors/${id}`),
};

// Door Channel API (master data)
export const doorChannelAPI = {
  getAll: (params) => api.get('/door-channels', { params }),
  getById: (id) => api.get(`/door-channels/${id}`),
  create: (data) => api.post('/door-channels', data),
  update: (id, data) => api.put(`/door-channels/${id}`, data),
  delete: (id, { force = false } = {}) =>
    api.delete(`/door-channels/${id}${force ? '?force=true' : ''}`),
  bulkDelete: (channelIds, { force = false } = {}) =>
    api.post(`/door-channels/bulk-delete${force ? '?force=true' : ''}`, { channelIds }),
  reassign: (fromName, toName) =>
    api.post('/door-channels/reassign', { fromName, toName })
};

// Door Sales API
export const doorSalesAPI = {
  getAll: (params) => api.get('/door-sales', { params }),
  getYears: () => api.get('/door-sales/years'),
  create: (data) => api.post('/door-sales', data),
  update: (id, data) => api.put(`/door-sales/${id}`, data),
  delete: (id) => api.delete(`/door-sales/${id}`),
  import: (formData) => api.post('/door-sales/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTemplate: () => api.get('/door-sales/template', { responseType: 'blob' }),
  resyncBrandFields: (cursor) => api.post('/door-sales/resync-brand-fields', { cursor: cursor || null }, { timeout: 120000 }),
  // "Select all filtered" actions — operate on the whole filtered set server-side.
  bulkDelete: (filter) => api.post('/door-sales/bulk-delete', filter, { timeout: 300000 }),
  // A year of door sales is a ~34MB CSV; give the download room on a slow line.
  exportAll: (params) => api.get('/door-sales/export', {
    params,
    responseType: 'blob',
    timeout: 900000,
    maxContentLength: Infinity,
  })
};

export const outpostSalesAPI = {
  getAll: (params) => api.get('/outpost-sales', { params }),
  getYears: () => api.get('/outpost-sales/years'),
  create: (data) => api.post('/outpost-sales', data),
  update: (id, data) => api.put(`/outpost-sales/${id}`, data),
  delete: (id) => api.delete(`/outpost-sales/${id}`),
  import: (formData) => api.post('/outpost-sales/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTemplate: () => api.get('/outpost-sales/template', { responseType: 'blob' }),
  exportToExcel: (params) => api.get('/outpost-sales/export', { params, responseType: 'blob' })
};

export const newProductLaunchAPI = {
  getAll: (params) => api.get('/new-product-launch-list', { params }),
  getYears: () => api.get('/new-product-launch-list/years'),
  import: (formData) => api.post('/new-product-launch-list/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteYear: (year) => api.delete(`/new-product-launch-list/year/${year}`),
  delete: (id) => api.delete(`/new-product-launch-list/${id}`)
};

export const arrivalPipelineAPI = {
  allProducts: (params) => api.get('/arrival-pipeline/all-products', { params }),
  novelties: (params) => api.get('/arrival-pipeline/novelties', { params })
};

// Door Sales + Outpost Sales ↔ Product Sales reconciliation. The whole-dataset
// rollups run long on a full-history check, hence the raised timeouts.
export const dataConsistencyAPI = {
  getYears: () => api.get('/data-consistency/sales/years'),
  getSalesConsistency: (params) => api.get('/data-consistency/sales', { params, timeout: 180000 }),
  getSalesConsistencyDetail: (params) => api.get('/data-consistency/sales/detail', { params, timeout: 120000 }),
  // Outpost Sales sits inside Door Sales; Product Sales checked against itself.
  getOutpostContainment: (params) => api.get('/data-consistency/outpost-containment', { params, timeout: 180000 }),
  getProductContinuity: (params) => api.get('/data-consistency/product-continuity', { params, timeout: 180000 }),
  // Saved runs, one collection for all three checks — always pass `checkType`.
  // Downloads and deletes are admin-only server-side. Saving a door-vs-product
  // run also captures the per-SKU breakdown, so it runs longer than the check.
  saveRun: (body) => api.post('/data-consistency/runs', body, { timeout: 600000 }),
  listRuns: (params) => api.get('/data-consistency/runs', { params }),
  getRun: (id) => api.get(`/data-consistency/runs/${id}`, { timeout: 120000 }),
  // `params` narrows the stored rows the same way the page's filters do, so a
  // download matches what was on screen.
  exportRun: (id, params) => api.get(`/data-consistency/runs/${id}/export`, { params, responseType: 'blob', timeout: 600000 }),
  deleteRun: (id) => api.delete(`/data-consistency/runs/${id}`)
};

// Brand Door Weight API
// Beauty Advisor roster
export const beautyAdvisorAPI = {
  getAll: (params) => api.get('/beauty-advisors', { params }),
  create: (data) => api.post('/beauty-advisors', data),
  update: (id, data) => api.put(`/beauty-advisors/${id}`, data),
  deactivate: (id) => api.delete(`/beauty-advisors/${id}`),
  reactivate: (id) => api.put(`/beauty-advisors/${id}`, { isActive: true }),
  // Sundora stores an advisor can be placed at, with advisor counts.
  getStores: () => api.get('/beauty-advisors/stores'),
  // formData carries `file`; previewImport writes nothing.
  previewImport: (formData) => api.post('/beauty-advisors/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // formData carries `file`, `deactivateMissing` and `reactivateListed` ('true'/'false').
  importRoster: (formData) => api.post('/beauty-advisors/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  exportRoster: () => api.get('/beauty-advisors/export', { responseType: 'blob' }),
};

// Which suppliers and brands an advisor serves. The door is not part of it: an advisor
// stands at one door at a time, so the roster already answers where.
// `month` is 'YYYY-MM' or 'start' (start of records): the first month a change applies
// to. The server defaults it to the current month. `rev` is the revision the pair was
// loaded at (null: no document); the server answers 409 when the pair is at another one.
export const baAssignmentAPI = {
  // params: asOf ('YYYY-MM'), advisor, supplier, door, includeInactive ('true' = every pair
  // with its full periods).
  getAll: (params) => api.get('/ba-assignments', { params }),
  // data: { advisor, supplier, brands, preferred, isActive, month, notes, rev }
  save: (data) => api.post('/ba-assignments', data),
  // rows: [{ advisor, serves, brands, preferred, rev }] for one supplier, all from `month`.
  saveForSupplier: (supplierId, rows, month) => api.post(`/ba-assignments/supplier/${supplierId}`, { month, rows }),
  // Ends the pair from `month`.
  deactivate: (id, month, rev) => api.delete(`/ba-assignments/${id}`, {
    params: {
      ...(month ? { month } : {}),
      ...(rev !== undefined ? { rev: rev === null ? 'null' : rev } : {}),
    },
  }),
  template: () => api.get('/ba-assignments/template', { responseType: 'blob' }),
  importAssignments: (formData) => api.post('/ba-assignments/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Beauty Advisor sales attribution: the share grid, fill settings, and the report
export const baAttributionAPI = {
  getDoors: (params) => api.get('/ba-attribution/doors', { params }),
  getGrid: (params) => api.get('/ba-attribution/grid', { params }),
  saveGrid: (data) => api.post('/ba-attribution/grid', data),
  // Suggested shares for brands that hold none yet; writes nothing.
  prefill: (params) => api.get('/ba-attribution/prefill', { params }),
  restampDesignations: (data) => api.post('/ba-attribution/restamp', data),
  saveSetting: (data) => api.post('/ba-attribution/settings', data),
  saveBrandSettings: (data) => api.post('/ba-attribution/brand-settings', data),
  getReport: (params) => api.get('/ba-attribution/report', { params }),
  copyMonth: (data) => api.post('/ba-attribution/copy-month', data),
  getBasket: (params) => api.get('/ba-attribution/basket', { params }),
  saveBasketTally: (data) => api.post('/ba-attribution/basket', data),
  // The selection's expected items per bill. data: { supplier, brands, brandGroups,
  // advisorItemsPerBill, doorItemsPerBill }; advisorItemsPerBill null or '' deletes it.
  saveItemsPerBill: (data) => api.post('/ba-attribution/basket/items-per-bill', data),
  copyBasketMonth: (data) => api.post('/ba-attribution/basket/copy-month', data),
  // The IPT target range per advisor tier (guidance only). data for save: { senior, preferred,
  // advisor } each { min, max }, or { reset: true } to go back to the defaults.
  getIptTargets: () => api.get('/ba-attribution/ipt-targets'),
  saveIptTargets: (data) => api.put('/ba-attribution/ipt-targets', data),
  basketTemplate: (params) => api.get('/ba-attribution/basket/template', { params, responseType: 'blob' }),
  importBasketTally: (formData) => api.post('/ba-attribution/basket/import', formData),
  getCoverage: (data) => api.post('/ba-attribution/coverage', data),
  exportRange: (data) => api.post('/ba-attribution/export', data, { responseType: 'blob' }),
  // The export page's saved filters. data: { name, designations, customerGroup,
  // onlyDoorsWithSales, brands, brandGroups, segments, suppliers }.
  getExportPresets: () => api.get('/ba-attribution/export-presets'),
  createExportPreset: (data) => api.post('/ba-attribution/export-presets', data),
  updateExportPreset: (id, data) => api.put(`/ba-attribution/export-presets/${id}`, data),
  deleteExportPreset: (id) => api.delete(`/ba-attribution/export-presets/${id}`),
};

export const brandDoorWeightAPI = {
  // Get all weights with filters
  getAll: (params) => api.get('/brand-door-weights', { params }),
  // Get summary of brands with weight status
  getSummary: (params) => api.get('/brand-door-weights/summary', { params }),
  // Get weights for specific brand and year
  getByBrandAndYear: (brandId, year) => api.get(`/brand-door-weights/brand/${brandId}/year/${year}`),
  // Get all weights for a brand (all years)
  getByBrand: (brandId) => api.get(`/brand-door-weights/brand/${brandId}`),
  // Get single record
  getById: (id) => api.get(`/brand-door-weights/${id}`),
  // Create new weight
  create: (data) => api.post('/brand-door-weights', data),
  // Bulk upsert
  bulkUpsert: (data) => api.post('/brand-door-weights/bulk', data),
  // Update weight
  update: (id, data) => api.put(`/brand-door-weights/${id}`, data),
  // Delete weight
  delete: (id) => api.delete(`/brand-door-weights/${id}`),
  // Copy year
  copyYear: (data) => api.post('/brand-door-weights/copy-year', data),
  // Validate weights
  validate: (brandId, year) => api.get(`/brand-door-weights/validate/${brandId}/${year}`),
  // Get doors for business plan
  getForBusinessPlan: (brandId, startYear, duration) =>
    api.get('/brand-door-weights/for-business-plan', {
      params: { brandId, year: startYear, duration }
    }),
  // The same handler, mounted under business plans as well (see businessPlanRoutes).
  // It reads `year`, not `startYear` — sending the latter meant the query carried no
  // year at all and the handler silently fell back to the current one.
  prePopulateDoors: (brandId, startYear, duration) =>
    api.get('/business-plans/pre-populate-doors', {
      params: { brandId, year: startYear, duration }
    })
};

// Sales Data API
export const salesDataAPI = {
  upload: (file, uploadedBy = 'System') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadedBy', uploadedBy);
    
    return api.post('/sales-data/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAll: (params) => api.get('/sales-data', { params }),
  getRanking: (params) => api.get('/sales-data/ranking', { params }),
  getRankingFilters: (params) => api.get('/sales-data/ranking/filters', { params }),
  delete: (id) => api.delete(`/sales-data/${id}`),
  bulkDelete: (ids) => api.post('/sales-data/bulk-delete', { ids })
};

// SOB Matrix API
export const sobMatrixAPI = {
  getByYear: (year) => api.get('/sob-matrix', { params: { year } }),
  getCalculated: (year, startMonth, endMonth) =>
    api.get('/sob-matrix/calculated', { params: { year, startMonth, endMonth } }),
  downloadTemplate: () => api.get('/sob-matrix/template', { responseType: 'blob' }),
  exportByYear: (year) => api.get('/sob-matrix/export', { params: { year }, responseType: 'blob' }),
  saveYear: (year, payload) => api.post('/sob-matrix/save-year', { year, ...payload }),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/sob-matrix/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Dashboard API
export const brandRankingAPI = {
  // Adjustments
  saveAdjustments: (adjustments, snapshotId, viewId, notes) => 
    api.post('/brand-ranking/adjustments', { adjustments, snapshotId, viewId, notes }),
  getAdjustments: (snapshotId, viewId) => 
    api.get('/brand-ranking/adjustments', { params: { snapshotId, viewId } }),
  
  // Snapshots
  createSnapshot: (data) => api.post('/brand-ranking/snapshots', data),
  getSnapshots: (includeInactive = false, viewId = null) => 
    api.get('/brand-ranking/snapshots', { params: { includeInactive, viewId } }),
  getSnapshot: (id) => api.get(`/brand-ranking/snapshots/${id}`),
  updateSnapshot: (id, data) => api.put(`/brand-ranking/snapshots/${id}`, data),
  deleteSnapshot: (id) => api.delete(`/brand-ranking/snapshots/${id}`),
  setDefaultSnapshot: (id) => api.post(`/brand-ranking/snapshots/${id}/set-default`),
  
  // Views
  createView: (data) => api.post('/brand-ranking/views', data),
  getViews: (includeInactive = false) => 
    api.get('/brand-ranking/views', { params: { includeInactive } }),
  getView: (id) => api.get(`/brand-ranking/views/${id}`),
  updateView: (id, data) => api.put(`/brand-ranking/views/${id}`, data),
  deleteView: (id) => api.delete(`/brand-ranking/views/${id}`),
  setDefaultView: (id) => api.post(`/brand-ranking/views/${id}/set-default`),
  
  // Export
  exportRanking: (params) => api.get('/brand-ranking/export', { 
    params,
    responseType: 'blob' // Important for file downloads
  })
};

export const dashboardAPI = {
  getMetrics: (params) => api.get('/dashboard/metrics', { params }),
  getPriceEvolution: () => api.get('/dashboard/price-evolution')
};

// Budget Projection API
export const budgetProjectionAPI = {
  // CRUD
  getAll: (params) => api.get('/budget-projections', { params }),
  getById: (id) => api.get(`/budget-projections/${id}`),
  create: (data) => api.post('/budget-projections', data),
  update: (id, data) => api.put(`/budget-projections/${id}`, data),
  delete: (id) => api.delete(`/budget-projections/${id}`),

  // Filter options
  getFilterOptions: () => api.get('/budget-projections/filters'),

  // Actuals management
  getActuals: (id) => api.get(`/budget-projections/${id}/actuals`),
  addActuals: (id, data) => api.post(`/budget-projections/${id}/actuals`, data),
  updateActual: (id, actualId, data) => api.put(`/budget-projections/${id}/actuals/${actualId}`, data),
  deleteActual: (id, actualId) => api.delete(`/budget-projections/${id}/actuals/${actualId}`),
  importActuals: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/budget-projections/${id}/actuals/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadActualsTemplate: (id) => api.get(`/budget-projections/${id}/actuals/template`, { responseType: 'blob' }),

  // Seasonal weights
  getSeasonalWeightPresets: () => api.get('/budget-projections/presets/seasonal-weights'),
  updateSeasonalWeights: (id, data) => api.put(`/budget-projections/${id}/seasonal-weights`, data),

  // Seasonal weight templates
  getSeasonalWeightTemplates: () => api.get('/budget-projections/seasonal-weight-templates'),
  createSeasonalWeightTemplate: (data) => api.post('/budget-projections/seasonal-weight-templates', data),
  updateSeasonalWeightTemplate: (id, data) => api.put(`/budget-projections/seasonal-weight-templates/${id}`, data),
  deleteSeasonalWeightTemplate: (id) => api.delete(`/budget-projections/seasonal-weight-templates/${id}`),

  // Door projections
  getDoorProjections: (id) => api.get(`/budget-projections/${id}/door-projections`),
  updateDoorProjections: (id, data) => api.put(`/budget-projections/${id}/door-projections`, data),
  syncDoorWeights: (id, data) => api.post(`/budget-projections/${id}/door-projections/sync-weights`, data),
  syncActualsToBase: (id) => api.post(`/budget-projections/${id}/door-projections/sync-base`),
  syncFromBusinessPlan: (id) => api.post(`/budget-projections/${id}/door-projections/sync-bp`),
  applyBulkGrowth: (id, data) => api.post(`/budget-projections/${id}/door-projections/apply-growth`, data),

  // A&P
  getAPData: (id) => api.get(`/budget-projections/${id}/ap`),
  updateAPData: (id, data) => api.put(`/budget-projections/${id}/ap`, data),

  // A&P Categories
  initializeAPCategories: (id) => api.post(`/budget-projections/${id}/ap/initialize-categories`),
  updateAPLineItem: (id, categoryCode, data) => api.put(`/budget-projections/${id}/ap/line-item/${categoryCode}`, data),
  resetAPCategories: (id) => api.post(`/budget-projections/${id}/ap/reset-categories`),

  // Calculations
  recalculate: (id) => api.post(`/budget-projections/${id}/recalculate`),
  getSummary: (id) => api.get(`/budget-projections/${id}/summary`),

  // Export
  exportToExcel: (id) => api.get(`/budget-projections/${id}/export/excel`, { responseType: 'blob' }),
  exportToPDF: (id) => api.get(`/budget-projections/${id}/export/pdf`, { responseType: 'blob' }),

  // Duplication
  duplicate: (id) => api.post(`/budget-projections/${id}/duplicate`),

  // Inventory & Stock
  getStockProjections: (id) => api.get(`/budget-projections/${id}/stock-projections`),
  getPurchaseRecommendations: (id) => api.get(`/budget-projections/${id}/recommendations`),
  convertRecommendationToPO: (id, data) => api.post(`/budget-projections/${id}/recommendations/convert`, data),
  getLinkedPurchaseOrders: (id) => api.get(`/budget-projections/${id}/purchase-orders`),
  getStockOnHand: (id) => api.get(`/budget-projections/${id}/inventory/stock-on-hand`),

  // =====================================================
  // PHASE 3: SCENARIOS
  // =====================================================
  getScenarios: (id) => api.get(`/budget-projections/${id}/scenarios`),
  updateScenario: (id, name, data) => api.put(`/budget-projections/${id}/scenarios/${name}`, data),
  calculateScenario: (id, name) => api.post(`/budget-projections/${id}/scenarios/${name}/calculate`),
  calculateAllScenarios: (id) => api.post(`/budget-projections/${id}/scenarios/calculate-all`),
  setActiveScenario: (id, name, year) => api.post(`/budget-projections/${id}/scenarios/${name}/activate`, year ? { year } : {}),
  approveScenario: (id, name) => api.post(`/budget-projections/${id}/scenarios/${name}/approve`),
  compareScenarios: (id, year) => api.get(`/budget-projections/${id}/scenarios/compare`, { params: { year } }),
  getScenarioChartData: (id, metric) => api.get(`/budget-projections/${id}/scenarios/chart-data`, { params: { metric } }),
  initializeDoorOverrides: (id, name) => api.post(`/budget-projections/${id}/scenarios/${name}/door-overrides`),

  // =====================================================
  // PHASE 3: PROFITABILITY
  // =====================================================
  getProfitability: (id, params) => api.get(`/budget-projections/${id}/profitability`, { params }),
  getWaterfallData: (id, params) => api.get(`/budget-projections/${id}/profitability/waterfall`, { params }),
  getProfitabilityTrend: (id, scenario) => api.get(`/budget-projections/${id}/profitability/trend`, { params: { scenario } }),
  compareProfitability: (id, year) => api.get(`/budget-projections/${id}/profitability/compare`, { params: { year } }),
  updateProfitabilityConfig: (id, data) => api.put(`/budget-projections/${id}/profitability/config`, data),
  getMarginAnalysis: (id, params) => api.get(`/budget-projections/${id}/profitability/margins`, { params }),

  // =====================================================
  // PHASE 3: PRICE SIMULATION
  // =====================================================
  runPriceSimulation: (id, data) => api.post(`/budget-projections/${id}/simulation/price`, data),
  runMultiScenarioSimulation: (id, data) => api.post(`/budget-projections/${id}/simulation/price/multi`, data),
  runSensitivityAnalysis: (id, data) => api.post(`/budget-projections/${id}/simulation/sensitivity`, data),
  getBreakEvenAnalysis: (id, params) => api.get(`/budget-projections/${id}/simulation/break-even`, { params }),
  getSimulationChartData: (id, params) => api.get(`/budget-projections/${id}/simulation/chart-data`, { params }),
  applyPriceSimulation: (id, data) => api.post(`/budget-projections/${id}/simulation/apply`, data),

  // =====================================================
  // PHASE 4: BUDGET VS ACTUAL (BvA)
  // =====================================================
  getBudgetVsActual: (id, params) => api.get(`/budget-projections/${id}/budget-vs-actual`, { params }),
  initializeBvA: (id, data) => api.post(`/budget-projections/${id}/budget-vs-actual/initialize`, data),
  updateBvAActuals: (id, year, month, data) => api.put(`/budget-projections/${id}/budget-vs-actual/${year}/${month}`, data),
  importBvAActuals: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/budget-projections/${id}/budget-vs-actual/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBvASummary: (id, params) => api.get(`/budget-projections/${id}/budget-vs-actual/summary`, { params }),
  syncBvAFromActuals: (id, data) => api.post(`/budget-projections/${id}/budget-vs-actual/sync-actuals`, data),
  pullBvAFromDoorSales: (id, data) => api.post(`/budget-projections/${id}/budget-vs-actual/pull-from-door-sales`, data),

  // =====================================================
  // PHASE 4: REVISION MANAGEMENT
  // =====================================================
  getRevisions: (id) => api.get(`/budget-projections/${id}/revisions`),
  createRevision: (id, data) => api.post(`/budget-projections/${id}/revisions`, data),
  getRevision: (id, revisionNumber) => api.get(`/budget-projections/${id}/revisions/${revisionNumber}`),
  updateRevision: (id, revisionNumber, data) => api.put(`/budget-projections/${id}/revisions/${revisionNumber}`, data),
  addRevisionAdjustment: (id, revisionNumber, data) => api.post(`/budget-projections/${id}/revisions/${revisionNumber}/adjustments`, data),
  submitRevision: (id, revisionNumber) => api.post(`/budget-projections/${id}/revisions/${revisionNumber}/submit`),
  approveRevision: (id, revisionNumber) => api.post(`/budget-projections/${id}/revisions/${revisionNumber}/approve`),
  rejectRevision: (id, revisionNumber, data) => api.post(`/budget-projections/${id}/revisions/${revisionNumber}/reject`, data),
  compareRevisions: (id, rev1, rev2) => api.get(`/budget-projections/${id}/revisions/compare`, { params: { rev1, rev2 } }),

  // =====================================================
  // PHASE 4: BRAND TARGET LINKING
  // =====================================================
  linkToBrandTarget: (id, data) => api.post(`/budget-projections/${id}/link-brand-target`, data),

  // =====================================================
  // SALES FOUNDATION: ACTUALS
  // =====================================================
  getActualsSummaryByYear: (id, year) => api.get(`/budget-projections/${id}/actuals/summary/${year}`),
  updateDoorActual: (id, doorId, data) => api.put(`/budget-projections/${id}/actuals/door/${doorId}`, data),
  addProductActuals: (id, doorId, data) => api.post(`/budget-projections/${id}/actuals/door/${doorId}/products`, data),
  getCoefficients: (id) => api.get(`/budget-projections/${id}/coefficients`),
  updateCoefficients: (id, data) => api.put(`/budget-projections/${id}/coefficients`, data),
  allocateActualsByWeight: (id, data) => api.post(`/budget-projections/${id}/actuals/allocate-by-weight`, data),
  pullFromDoorSales: (id, year) => api.post(`/budget-projections/${id}/actuals/pull-from-door-sales`, { year }),

  // =====================================================
  // CURRENCY CONVERSION
  // =====================================================
  convertCurrency: (id, data) => api.post(`/budget-projections/${id}/convert-currency`, data),

  // =====================================================
  // DATA REPAIR
  // =====================================================
  repairDoorProjections: (id) => api.post(`/budget-projections/${id}/repair-door-projections`),

  // =====================================================
  // PRODUCT SALES MANAGEMENT
  // =====================================================
  getProductSales: (id, params) => api.get(`/budget-projections/${id}/product-sales`, { params }),
  getProductSalesSummary: (id, params) => api.get(`/budget-projections/${id}/product-sales/summary`, { params }),
  getProductSalesImports: (id) => api.get(`/budget-projections/${id}/product-sales/imports`),
  addProductSale: (id, data) => api.post(`/budget-projections/${id}/product-sales`, data),
  updateProductSale: (id, saleId, data) => api.put(`/budget-projections/${id}/product-sales/${saleId}`, data),
  deleteProductSale: (id, saleId) => api.delete(`/budget-projections/${id}/product-sales/${saleId}`),
  deleteProductSalesForPeriod: (id, year, month) => api.delete(`/budget-projections/${id}/product-sales/period/${year}/${month}`),
  importProductSales: (id, file, options) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', options.year);
    formData.append('month', options.month);
    formData.append('replaceExisting', options.replaceExisting !== false);
    return api.post(`/budget-projections/${id}/product-sales/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadProductSalesTemplate: (id) => api.get(`/budget-projections/${id}/product-sales/template`, { responseType: 'blob' }),
  syncProductSalesToSummary: (id) => api.post(`/budget-projections/${id}/product-sales/sync-summary`),
  exportProductSales: (id, params) => api.get(`/budget-projections/${id}/product-sales/export`, { params, responseType: 'blob' })
};

// =====================================================
// PROJECTIONS V2 API
// =====================================================
export const projectionV2API = {
  // CRUD
  getAll: (params) => api.get('/projections-v2', { params }),
  getById: (id) => api.get(`/projections-v2/${id}`),
  create: (data) => api.post('/projections-v2', data),
  update: (id, data) => api.put(`/projections-v2/${id}`, data),
  delete: (id) => api.delete(`/projections-v2/${id}`),
  duplicate: (id) => api.post(`/projections-v2/${id}/duplicate`),

  // Seasonal weights
  // params.years: '2024,2025' adds those years to the Bangladesh Retail preset's yearlyWeights.
  getSeasonalWeightPresets: (params) => api.get('/projections-v2/presets/seasonal-weights', { params }),
  updateSeasonalWeights: (id, data) => api.put(`/projections-v2/${id}/seasonal-weights`, data),

  // Scenarios
  getScenarios: (id) => api.get(`/projections-v2/${id}/scenarios`),
  updateScenario: (id, name, data) => api.put(`/projections-v2/${id}/scenarios/${name}`, data),
  updateAllScenarios: (id, data) => api.put(`/projections-v2/${id}/scenarios`, data),
  setActiveScenario: (id, name, year) => api.post(`/projections-v2/${id}/scenarios/${name}/activate`, year ? { year } : {}),
  calculateScenario: (id, name) => api.post(`/projections-v2/${id}/scenarios/${name}/calculate`),
  calculateAllScenarios: (id) => api.post(`/projections-v2/${id}/scenarios/calculate-all`),
  compareScenarios: (id, year) => api.get(`/projections-v2/${id}/scenarios/compare`, { params: { year } }),
  getScenarioChartData: (id, metric) => api.get(`/projections-v2/${id}/scenarios/chart-data`, { params: { metric } }),

  // Actuals
  getActualsForBP: (brand, market, axes, startYear, endYear, fxRate, currency) =>
    api.get('/projections-v2/actuals-for-bp', {
      params: { brand, market, axes: Array.isArray(axes) ? axes.join(',') : axes, startYear, endYear, fxRate, currency }
    }),
  getActuals: (id, year) => api.get(`/projections-v2/${id}/actuals`, { params: year ? { year } : {} }),
  upsertActuals: (id, data) => api.post(`/projections-v2/${id}/actuals`, data),
  pullActuals: (id, params) => {
    if (isMongoId(id)) {
      return api.post(`/projections-v2/${id}/actuals/pull`, params || {});
    }
    return api.post('/projections-v2/actuals/pull', params || {});
  },
  deleteActuals: (id, year) => api.delete(`/projections-v2/${id}/actuals/${year}`),

  // Summary & Recalculate
  getSummary: (id) => api.get(`/projections-v2/${id}/summary`),
  recalculate: (id) => api.post(`/projections-v2/${id}/recalculate`),

  // Door Breakdown (derived view of the brand projection per door)
  getDoorBreakdown: (id, params) =>
    api.get(`/projections-v2/${id}/door-breakdown`, { params: params || {} }),
  getDoorMonthlyBreakdown: (id, doorId, params) =>
    api.get(`/projections-v2/${id}/door-breakdown/${doorId}/monthly`, { params: params || {} }),

  // Status Workflow
  updateStatus: (id, data) => api.put(`/projections-v2/${id}/status`, data),
  getStatusHistory: (id) => api.get(`/projections-v2/${id}/status-history`),
  addReviewNote: (id, data) => api.post(`/projections-v2/${id}/review-notes`, data)
};

// Brand Target API
export const brandTargetAPI = {
  // CRUD
  getAll: (params) => api.get('/brand-targets', { params }),
  getById: (id) => api.get(`/brand-targets/${id}`),
  create: (data) => api.post('/brand-targets', data),
  update: (id, data) => api.put(`/brand-targets/${id}`, data),
  delete: (id) => api.delete(`/brand-targets/${id}`),

  // Filter options
  getFilterOptions: () => api.get('/brand-targets/filters'),

  // Dashboard & Reports
  getDashboardSummary: (params) => api.get('/brand-targets/dashboard', { params }),
  getAtRiskTargets: (params) => api.get('/brand-targets/at-risk', { params }),
  getByStatus: (status, params) => api.get(`/brand-targets/status/${status}`, { params }),

  // Negotiation Workflow
  submitCounterProposal: (id, data) => api.post(`/brand-targets/${id}/counter-proposal`, data),
  acceptTarget: (id, data) => api.post(`/brand-targets/${id}/accept`, data),
  rejectTarget: (id, data) => api.post(`/brand-targets/${id}/reject`, data),
  finalizeTarget: (id) => api.post(`/brand-targets/${id}/finalize`),
  addNote: (id, data) => api.post(`/brand-targets/${id}/note`, data),

  // Performance & Linking
  updateYTDPerformance: (id, data) => api.put(`/brand-targets/${id}/ytd-performance`, data),
  linkToProjection: (id, data) => api.post(`/brand-targets/${id}/link-projection`, data),

  // Bulk Operations
  syncFromProjections: (data) => api.post('/brand-targets/sync-from-projections', data)
};

// AI Assistant API
export const assistantAPI = {
  // Check if AI is configured
  checkStatus: () => api.get('/assistant/status'),

  // Get available sections
  getSections: () => api.get('/assistant/sections'),

  // Get Bangladesh macroeconomic data (legacy)
  getBangladeshMacroData: () => api.get('/assistant/macro-data/bangladesh'),

  // Country comparison endpoints
  getAvailableCountries: () => api.get('/assistant/countries'),
  getCountryData: (countryCode) => api.get(`/assistant/countries/${countryCode}`),
  getCountryComparison: (countryCodes) =>
    api.get('/assistant/country-comparison', { params: { countries: countryCodes.join(',') } }),

  // Real-time data fetching (Perplexity/OpenAI)
  fetchRealTimeData: (query, countries, category) =>
    api.post('/assistant/fetch-real-time', { query, countries, category }),

  // Chat about market data (AI agent for country comparison)
  chatMarketData: (messages, context) =>
    api.post('/assistant/chat-market', { messages, context }),

  // =====================================================
  // MARKET DATA MANAGEMENT (AI-powered)
  // =====================================================

  // Chat with actions (parses intent and can modify data)
  chatMarketDataWithActions: (messages, context) =>
    api.post('/assistant/chat-market-action', { messages, context }),

  // Update market data (confirm pending update or manual update)
  updateMarketData: (data) =>
    api.post('/assistant/market-data/update', data),

  // Add new market data (new country or metric)
  addMarketData: (dataPoints) =>
    api.post('/assistant/market-data/add', { dataPoints }),

  // Fetch live market data using Perplexity
  fetchLiveMarketData: (query, countries, category) =>
    api.post('/assistant/market-data/fetch-live', { query, countries, category }),

  // Get custom market data for a country
  getCustomMarketData: (countryCode) =>
    api.get(`/assistant/market-data/custom/${countryCode}`),

  // Get merged market data (baseline + custom overrides)
  getMergedMarketData: (countryCode) =>
    api.get(`/assistant/market-data/merged/${countryCode}`),

  // Get all custom countries
  getCustomCountries: () =>
    api.get('/assistant/market-data/custom-countries'),

  // Get market data history
  getMarketDataHistory: (params) =>
    api.get('/assistant/market-data/history', { params }),

  // Revert market data to a previous version
  revertMarketData: (id, versionIndex) =>
    api.post(`/assistant/market-data/revert/${id}`, { versionIndex }),

  // Generate full business plan insights
  generateInsights: (businessPlanId, context) =>
    api.post(`/assistant/business-plans/${businessPlanId}/insights`, context),

  // Generate a specific section
  generateSection: (businessPlanId, sectionName, context) =>
    api.post(`/assistant/business-plans/${businessPlanId}/section`, {
      sectionName,
      ...context
    }),

  // Quick insights without saved business plan
  generateQuickInsights: (data) =>
    api.post('/assistant/quick-insights', data),

  // Refine a section with user feedback
  refineSection: (data) =>
    api.post('/assistant/refine-section', data),

  // Refine a specific SWOT column (add more items)
  refineSwotColumn: (data) =>
    api.post('/assistant/refine-swot-column', data),

  // Edit a specific SWOT item (reword/refine existing item)
  editSwotItem: (data) =>
    api.post('/assistant/edit-swot-item', data),

  // Chat with assistant for iterative improvements
  chat: (messages, context) =>
    api.post('/assistant/chat', { messages, context }),

  // =====================================================
  // SAVED AI RESULTS
  // =====================================================

  // Create a new saved result
  createSavedResult: (data) =>
    api.post('/assistant/saved-results', data),

  // Get saved results (paginated, filtered)
  getSavedResults: (params) =>
    api.get('/assistant/saved-results', { params }),

  // Get a single saved result by ID
  getSavedResultById: (id) =>
    api.get(`/assistant/saved-results/${id}`),

  // Update a saved result
  updateSavedResult: (id, data) =>
    api.put(`/assistant/saved-results/${id}`, data),

  // Delete a saved result
  deleteSavedResult: (id) =>
    api.delete(`/assistant/saved-results/${id}`),

  // Get saved results for a specific business plan
  getSavedResultsByPlan: (businessPlanId) =>
    api.get(`/assistant/saved-results/business-plan/${businessPlanId}`),

  // Archive/unarchive a saved result
  archiveSavedResult: (id, archived) =>
    api.patch(`/assistant/saved-results/${id}/archive`, { archived })
};

// ============================================
// PURCHASE ORDER API
// ============================================
export const purchaseOrderAPI = {
  // CRUD
  getAll: (params, signal) => api.get('/purchase-orders', { params, signal }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  deleteAll: () => api.delete('/purchase-orders/all'),

  // Filters & Stats
  getFilterOptions: () => api.get('/purchase-orders/filters'),
  getStats: (params) => api.get('/purchase-orders/stats', { params }),

  // Master Data "Purchase Orders" visual rep
  getSummary: (params) => api.get('/purchase-orders/summary', { params }),
  getSyncReports: (params) => api.get('/purchase-orders/sync-reports', { params }),
  getGrouped: (params) => api.get('/purchase-orders/grouped', { params }),
  getByReference: (reference, params) =>
    api.get(`/purchase-orders/by-reference/${encodeURIComponent(reference)}`, { params }),

  // Deliveries
  recordDelivery: (id, data) => api.post(`/purchase-orders/${id}/deliveries`, data),
  getDeliveries: (id) => api.get(`/purchase-orders/${id}/deliveries`),

  // Pipeline
  getMonthlyPipeline: (brand, market, params) =>
    api.get(`/purchase-orders/pipeline/${brand}/${market}`, { params }),
  getPendingDeliveries: (brand, market) =>
    api.get(`/purchase-orders/pending/${brand}/${market}`),
  getOverdueOrders: (params) =>
    api.get('/purchase-orders/overdue', { params }),

  // Import/Export
  import: (formData) => api.post('/purchase-orders/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTemplate: () => api.get('/purchase-orders/template/download', {
    responseType: 'blob'
  }),
  export: (id) => api.get(`/purchase-orders/${id}/export`, {
    responseType: 'blob'
  }),

  // From Recommendation
  createFromRecommendation: (data) =>
    api.post('/purchase-orders/from-recommendation', data),

  // InvenTree Sync — a manual pull fetches the whole history (that is what makes
  // it able to refresh POs older than the scheduled sync's 90-day window), so it
  // runs for minutes. The client default of 60s aborts mid-sync and reports a
  // failure for a pull that is still running. nginx proxy_read_timeout must allow
  // the same.
  // Returns as soon as the run is started; the sync itself finishes on the
  // server and the page polls the sync report for the outcome.
  syncFromInvenTree: (data) =>
    api.post('/purchase-orders/sync-inventree', data, { timeout: 30000 }),
  resetInvenTreeSync: () =>
    api.post('/purchase-orders/sync-reset', {}, { timeout: 30000 }),

  // InvenTree Report (flat data)
  getInvenTreeReport: (params) =>
    api.get('/purchase-orders/inventree-report', { params }),

  // Inventory Timeline
  getInventoryTimeline: (params) =>
    api.get('/purchase-orders/inventory-timeline', { params }),
  getInventoryTimelineFilters: (params) =>
    api.get('/purchase-orders/inventory-timeline/filters', { params })
};

// ============================================
// CUSTOMER ARRIVAL API (Shipment Timeline)
// ============================================
export const customerArrivalAPI = {
  getShipmentTimeline: (params) =>
    api.get('/customer-arrivals/shipment-timeline', { params }),
  getFilterOptions: (params) =>
    api.get('/customer-arrivals/filters', { params }),
  syncFromInvenTree: () =>
    api.post('/customer-arrivals/sync')
};

// ============================================
// INVENTORY API
// ============================================
export const inventoryAPI = {
  // Snapshots
  getSnapshots: (params) => api.get('/inventory/snapshots', { params }),
  getSnapshotById: (id) => api.get(`/inventory/snapshots/${id}`),
  createSnapshot: (data) => api.post('/inventory/snapshots', data),
  updateSnapshot: (id, data) => api.put(`/inventory/snapshots/${id}`, data),
  deleteSnapshot: (id) => api.delete(`/inventory/snapshots/${id}`),

  // Calculations
  calculateSnapshots: (brand, market, data) =>
    api.post(`/inventory/calculate/${brand}/${market}`, data),

  // Alerts
  getAlerts: (params) => api.get('/inventory/alerts', { params }),
  getAlertsSummary: (params) => api.get('/inventory/alerts/summary', { params }),
  acknowledgeAlert: (snapshotId, alertId) =>
    api.put(`/inventory/alerts/${snapshotId}/acknowledge`, { alertId }),
  bulkAcknowledgeAlerts: (alertIds) =>
    api.put('/inventory/alerts/bulk-acknowledge', { alertIds }),

  // Stock Flow
  getStockFlow: (brand, market, params) =>
    api.get(`/inventory/flow/${brand}/${market}`, { params }),
  getCurrentPosition: (brand, market) =>
    api.get(`/inventory/position/${brand}/${market}`),

  // Reports
  getCoverageReport: (params) => api.get('/inventory/reports/coverage', { params }),
  getCoverageTrend: (params) => api.get('/inventory/reports/coverage-trend', { params }),
  getTurnoverReport: (params) => api.get('/inventory/reports/turnover', { params }),

  // Sync from Product Sales
  syncFromProductSales: (data) => api.post('/inventory/sync-from-product-sales', data),

  // Brand Dashboard
  getBrandDashboard: (params, signal) => api.get('/inventory/brand-dashboard', { params, signal })
};

// ============================================
// PRODUCT LINE API
// ============================================
// ============================================
// PRODUCT SALES API (Standalone by Brand)
// ============================================
export const productSalesAPI = {
  // Brand Overview
  getBrandsWithSalesData: () => api.get('/product-sales/brands'),
  getBrandSummary: (brandId, params) => api.get(`/product-sales/brands/${brandId}/summary`, { params }),

  // Product Sales CRUD
  getAll: (params) => api.get('/product-sales', { params }),
  getByBrand: (brandId, params) => api.get(`/product-sales/brands/${brandId}`, { params }),
  addProductSale: (brandId, data) => api.post(`/product-sales/brands/${brandId}`, data),
  updateProductSale: (saleId, data) => api.put(`/product-sales/${saleId}`, data),
  deleteProductSale: (saleId) => api.delete(`/product-sales/${saleId}`),
  deleteForPeriod: (brandId, year, month) => api.delete(`/product-sales/brands/${brandId}/period/${year}/${month}`),
  // Delete every row matching the filters (server-side, so it spans pages).
  bulkDeleteByFilter: (filter) => api.post('/product-sales/bulk-delete', filter, { timeout: 300000 }),

  // Import/Export
  importFromExcel: (brandId, file, options) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', options.year);
    formData.append('month', options.month);
    formData.append('replaceExisting', options.replaceExisting !== false);
    return api.post(`/product-sales/brands/${brandId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTemplate: () => api.get('/product-sales/template', { responseType: 'blob' }),
  importBulkFromExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('replaceExisting', options.replaceExisting !== false);
    if (options.createMissingBrands) formData.append('createMissingBrands', 'true');
    if (options.fallbackBrand) formData.append('fallbackBrand', options.fallbackBrand);
    if (options.fallbackYear) formData.append('fallbackYear', options.fallbackYear);
    if (options.fallbackMonth) formData.append('fallbackMonth', options.fallbackMonth);
    // A combined YTD file is ~76,000 rows and runs for minutes; the client default
    // of 60s aborts mid-write and reports a failure for an import that completes.
    // nginx proxy_read_timeout must allow the same.
    return api.post('/product-sales/import-bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 900000
    });
  },
  // GA group workbook: one sheet per brand (brand resolved from the sheet name).
  importGaGroupFile: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('replaceExisting', options.replaceExisting !== false);
    if (options.year) formData.append('year', options.year);
    // Same scale as the bulk import above — a group workbook carries every brand
    // in the group across every generated month.
    return api.post('/product-sales/import-ga', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 900000
    });
  },
  downloadBulkTemplate: () => api.get('/product-sales/template-bulk', { responseType: 'blob' }),
  exportToExcel: (brandId, params) => api.get(`/product-sales/brands/${brandId}/export`, { params, responseType: 'blob' }),
  // The rows matching the filters, across brands (admin, one year at a time).
  // A year of every brand is ~100k rows, so the server streams it and the client
  // default timeout is far too short to see it through.
  exportFiltered: (params) => api.get('/product-sales/export', {
    params,
    responseType: 'blob',
    timeout: 900000,
    maxContentLength: Infinity,
  }),
  getImportHistory: (brandId) => api.get(`/product-sales/brands/${brandId}/imports`),

  // Projection Sync
  syncToProjection: (projectionId) => api.post(`/product-sales/sync-to-projection/${projectionId}`),
  getSyncStatus: (projectionId) => api.get(`/product-sales/sync-status/${projectionId}`),

  // Brand YoY Monthly Report
  getYoyReport: (brandId, year, month) =>
    api.get(`/product-sales/brands/${brandId}/yoy-report`, { params: { year, month } }),
  downloadYoyReport: (brandId, year, month) =>
    api.get(`/product-sales/brands/${brandId}/yoy-report`, {
      params: { year, month, format: 'xlsx' },
      responseType: 'blob'
    }),
  getYoyAudit: (brandId, year, month) =>
    api.get(`/product-sales/brands/${brandId}/yoy-report/audit`, { params: { year, month } }),
  getYoyAvailablePeriods: (brandId) =>
    api.get(`/product-sales/brands/${brandId}/yoy-report/periods`)
};

// ============================================
// GA EXTRACTION API (pull Generate Analysis group files → Product Sales)
// ============================================
/*
 * The signed-in user's own inbox. Every call is scoped to them by the server, so
 * there is nothing to pass but paging.
 */
export const notificationAPI = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  markAllSeen: () => api.post('/notifications/seen'),
};

export const gaExtractionAPI = {
  // A dry run answers with its preview. A writing pull answers 202 { jobId } at once and
  // runs in the background; poll getJobStatus.
  triggerSync: (opts) => api.post('/ga-extraction/sync', opts, { timeout: 300000 }),
  // Starts a background run and answers 202 { jobId, total }; poll getJobStatus.
  triggerGenerate: (opts) => api.post('/ga-extraction/generate', opts),
  // Progress/outcome of a background GA job: { jobId } or { operation } (generate, pull,
  // save-batch, sync-batch, submit-batch, use-platform, discard-stale) for the latest of that kind.
  getJobStatus: (params) => api.get('/ga-extraction/job-status', { params }),
  getBrandGroups: () => api.get('/ga-extraction/brand-groups'),
  getBrands: () => api.get('/ga-extraction/brands'),
  listGas: (params) => api.get('/ga-extraction/list', { params }),
  downloadGaFile: (gaName, params) => api.get(`/ga-extraction/download/${encodeURIComponent(gaName)}`, { params, responseType: 'blob', timeout: 180000 }),
  previewGa: (gaName, params) => api.get(`/ga-extraction/preview/${encodeURIComponent(gaName)}`, { params, timeout: 180000 }),
  uploadEdited: (file, { gaName, submit } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('gaName', gaName);
    if (submit) fd.append('submit', 'true');
    return api.post('/ga-extraction/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  // GA write jobs. One batch runs at a time on the server: each start answers 202 { jobId },
  // or 409 { message, jobId, operation } naming the batch already running. Follow a job with
  // getJobStatus({ jobId }) or ({ operation }). Shapes: docs/specs/ga-review/GA_BACKGROUND_JOBS.md.
  // Save to the app: [{ gaName, edits: [{ sheet, itemCode, month, year, qtySold }] }].
  startSaveBatch: (gas) => api.post('/ga-extraction/save-batch', { gas }),
  // Upload each GA's saved-not-synced edits and verify the file; GAs stay Drafts. ['GA-1', ...].
  startSyncBatch: (gas) => api.post('/ga-extraction/sync-batch', { gas }),
  // Submit each GA whose sync was verified, then check the platform's sales records. ['GA-1', ...].
  // `recheckOnly: true` (Check sales / Re-check sales) only checks GAs already submitted and
  // never submits a Draft.
  startSubmitBatch: (gas, { recheckOnly = false } = {}) => api.post('/ga-extraction/submit-batch', recheckOnly ? { gas, recheckOnly: true } : { gas }),
  // The GA's waiting edits against the platform's current file:
  // { gaName, docstatus, fileSource, pending, differ, examples }. Reads the file, so a long timeout.
  previewUsePlatform: (gaName) => api.post('/ga-extraction/use-platform', { gaName }, { timeout: 180000 }),
  // Reads the platform's current file into the app and discards the waiting edits; 202 { jobId }.
  startUsePlatform: (gaName) => api.post('/ga-extraction/use-platform', { gaName, confirm: true }),
  // The GA's waiting edits its current file has no row for: { gaName, docstatus, fileSource,
  // pending, stale: { count, sheets, edits } }. Reads the file, so a long timeout.
  previewDiscardStale: (gaName) => api.post('/ga-extraction/discard-stale', { gaName }, { timeout: 180000 }),
  // Removes the confirmed waiting edits ([{ sheet, itemCode, month, year }]) that the file
  // still has no row for when the job runs; 202 { jobId }.
  startDiscardStale: (gaName, keys) => api.post('/ga-extraction/discard-stale', { gaName, confirm: true, keys }),
  // Delete draft GA documents (admin). Takes a list; each name gets its own verdict.
  deleteGas: (gaNames) => api.post('/ga-extraction/delete', { gaNames }, { timeout: 300000 }),
  // Per-brand review notes for one month of a GA.
  // SKUs marked as running an activation that month (not GA-scoped).
  getItemMarks: (params) => api.get('/ga-extraction/item-marks', { params }),
  saveItemMarks: (body) => api.put('/ga-extraction/item-marks', body),
  getIncomingOverrides: (params) => api.get('/ga-extraction/incoming-overrides', { params }),
  saveIncomingOverride: (body) => api.put('/ga-extraction/incoming-overrides', body),
  // Notes and comments belong to a brand's month, not to a GA. gaName in a body is
  // provenance only: the GA the reviewer was looking at when they wrote it.
  getBrandNotes: ({ month, year }) => api.get('/ga-extraction/notes', { params: { month, year } }),
  saveBrandNote: (body) => api.put('/ga-extraction/notes', body),
  // The review board's months of cover for one month: [{ scope, key, n }], scope 'document'
  // (key ''), 'supplier' or 'brand'. A save sets one: { month, year, scope, key, n }; n null
  // deletes it. `config` is passed to axios (the keepalive write sent as the page goes).
  getCoverSettings: ({ month, year }) => api.get('/ga-extraction/cover-settings', { params: { month, year } }),
  saveCoverSetting: (body, config) => api.put('/ga-extraction/cover-settings', body, config),
  getBrandComments: ({ month, year }) => api.get('/ga-extraction/comments', { params: { month, year } }),
  addBrandComment: (body) => api.post('/ga-extraction/comments', body),
  updateBrandComment: (id, body) => api.put(`/ga-extraction/comments/${encodeURIComponent(id)}`, body),
  deleteBrandComment: (id) => api.delete(`/ga-extraction/comments/${encodeURIComponent(id)}`),
  // The GAs a review covers, with their metadata: { gas: 'GA-1,GA-2' } or { month, year }
  // (the latest Completed GA per brand group for that month).
  getReviewSet: (params) => api.get('/ga-extraction/review-set', { params }),
  // Records that a set was opened, so it can be resumed: { gas: [{ name, brandGroup }], month, year }.
  touchReview: (body) => api.post('/ga-extraction/reviews', body),
  // { inProgress, recent }: recorded sets with saved-not-synced edits, and other recently opened sets.
  getReviews: () => api.get('/ga-extraction/reviews'),
  // Mark complete: takes a recorded set (by its id from getReviews) off Recently opened until
  // it is opened again. Answers { id, gaNames, month, year, completedAt, completedByName };
  // 409 { message, gaNames } while any of its GAs holds saved-not-synced edits, and 409
  // { message } when the set is opened again while it is being marked.
  completeReview: (id) => api.post(`/ga-extraction/reviews/${encodeURIComponent(id)}/complete`),
  // Undo of completeReview.
  undoCompleteReview: (id) => api.delete(`/ga-extraction/reviews/${encodeURIComponent(id)}/complete`),
  // Excel export of a review: { gas: ['GA-1', ...], month, year, edits: [unsaved edits] }.
  // Answers 202 { jobId }; poll getReviewExport, then fetch the file.
  startReviewExport: (body) => api.post('/ga-extraction/export', body),
  getReviewExport: (jobId) => api.get(`/ga-extraction/export/${encodeURIComponent(jobId)}`),
  downloadReviewExport: (jobId) => api.get(`/ga-extraction/export/${encodeURIComponent(jobId)}/file`, { responseType: 'blob', timeout: 180000 }),
  // Reads an edited review workbook back for one month: { file, month, year }. Read-only;
  // the page decides what to apply.
  readReviewImport: (file, { month, year }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('month', month);
    fd.append('year', String(year));
    return api.post('/ga-extraction/import', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
  },
  getPendingSync: () => api.get('/ga-extraction/pending'),
  // One GA's sync status: saved-not-synced edits, the file the app reads, Draft or Submitted,
  // the last verified sync and the last submit's sales check. A quick read, so a short timeout.
  getSyncStatus: (gaName) => api.get('/ga-extraction/sync-status', { params: { gaName }, timeout: 20000 }),
  // Force-clear a stuck/orphaned GA sync lock (admin recovery).
  resetSync: (gaName) => api.post('/ga-extraction/sync-reset', { gaName }, { timeout: 20000 }),
  getBaselines: (payload) => api.post('/ga-extraction/baselines', payload),
};

// ============================================
// GA NEW PO GENERATION API
// ============================================
export const gaNewPoAPI = {
  getStatus: () => api.get('/ga-new-po/status'),
  // Reference imports write up to ~100k rows (the Salesforce extract) — long timeout;
  // the nginx snippet's /api/ga-new-po/import/ block allows 900 s.
  importReference: (kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/ga-new-po/import/${kind}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
  },
  // Reference exports can run to tens of thousands of rows — long timeout, blob.
  exportReference: (kind) =>
    api.get(`/ga-new-po/export/${kind}`, { responseType: 'blob', timeout: 300000 }),
  // ~95k products over ~48 pages: seconds against the live org, but the timeout
  // allows for a slow link rather than failing a run that would have finished.
  syncSalesforce: () => api.post('/ga-new-po/sync/salesforce', {}, { timeout: 300000 }),
  getPreview: (params) => api.get('/ga-new-po/preview', { params, timeout: 180000 }),
  getDatePreview: (params) => api.get('/ga-new-po/date-preview', { params, timeout: 180000 }),
  exportDatePreview: (params) =>
    api.get('/ga-new-po/date-preview/export', { params, responseType: 'blob', timeout: 180000 }),
  generate: (payload) => api.post('/ga-new-po/generate', payload, { timeout: 600000 }),
  listJobs: (params) => api.get('/ga-new-po/jobs', { params }),
  // Fetched when a run is opened, not with the listing — the rows run to
  // hundreds and most runs are never opened.
  getJobFindings: (jobId) => api.get(`/ga-new-po/jobs/${jobId}/findings`),
  downloadJobReport: (jobId, part) =>
    api.get(`/ga-new-po/jobs/${jobId}/report/${part}`, { responseType: 'blob', timeout: 180000 }),
  deleteJob: (jobId) => api.delete(`/ga-new-po/jobs/${jobId}`),
  downloadJob: (jobId) => api.get(`/ga-new-po/download/${jobId}`, { responseType: 'blob', timeout: 180000 }),
  downloadFile: (jobId, fileName) =>
    api.get(`/ga-new-po/download/${jobId}/${encodeURIComponent(fileName)}`, { responseType: 'blob', timeout: 180000 }),
  listExcludedSuppliers: () => api.get('/ga-new-po/excluded-suppliers'),
  addExcludedSupplier: (name, reason) => api.post('/ga-new-po/excluded-suppliers', { name, reason }),
  removeExcludedSupplier: (id) => api.delete(`/ga-new-po/excluded-suppliers/${id}`),
  listUploaded: () => api.get('/ga-new-po/uploaded'),
  markUploaded: (invenTreePos, source = 'app') => api.post('/ga-new-po/uploaded/mark', { invenTreePos, source }),
  // Free text — PO numbers separated by spaces, commas or new lines.
  markUploadedText: (text, source = 'manual') => api.post('/ga-new-po/uploaded/mark', { text, source }),
  clearUploaded: (po) => api.delete(`/ga-new-po/uploaded/${encodeURIComponent(po)}`),
};

// ============================================
// MARKETING CATEGORY API
// ============================================
export const marketingCategoryAPI = {
  getAll: (params) => api.get('/marketing-categories', { params }),
  getHierarchy: (params) => api.get('/marketing-categories/hierarchy', { params }),
  getActive: (params) => api.get('/marketing-categories/active', { params }),
  getById: (id) => api.get(`/marketing-categories/${id}`),
  create: (data) => api.post('/marketing-categories', data),
  update: (id, data) => api.put(`/marketing-categories/${id}`, data),
  delete: (id) => api.delete(`/marketing-categories/${id}`),
  downloadTemplate: () => api.get('/marketing-categories/template', { responseType: 'blob' }),
  exportAll: () => api.get('/marketing-categories/export', { responseType: 'blob' }),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/marketing-categories/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// ============================================
// MARKETING ITEM API
// ============================================
export const marketingItemAPI = {
  getAll: (params) => api.get('/marketing-items', { params }),
  getActive: (params) => api.get('/marketing-items/active', { params }),
  getByCategory: (categoryId) => api.get(`/marketing-items/by-category/${categoryId}`),
  getById: (id) => api.get(`/marketing-items/${id}`),
  create: (data) => api.post('/marketing-items', data),
  update: (id, data) => api.put(`/marketing-items/${id}`, data),
  delete: (id) => api.delete(`/marketing-items/${id}`),
  bulkDelete: (itemIds) => api.post('/marketing-items/bulk-delete', { itemIds }),
  downloadTemplate: () => api.get('/marketing-items/template', { responseType: 'blob' }),
  exportAll: () => api.get('/marketing-items/export', { responseType: 'blob' }),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/marketing-items/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// ============================================
// CONTRACTOR API
// ============================================
// ============================================
// MARKETING EXPENSE API
// ============================================
export const marketingExpenseAPI = {
  getAll: (params, config = {}) => api.get('/marketing-expenses', { params, ...config }),
  getByBudget: (budgetId) => api.get(`/marketing-expenses/by-budget/${budgetId}`),
  getById: (id) => api.get(`/marketing-expenses/${id}`),
  create: (data) => api.post('/marketing-expenses', data),
  update: (id, data) => api.put(`/marketing-expenses/${id}`, data),
  delete: (id) => api.delete(`/marketing-expenses/${id}`),
  bulkCreate: (expenses) => api.post('/marketing-expenses/bulk', { expenses }),
  bulkDelete: (expenseIds) => api.post('/marketing-expenses/bulk-delete', { expenseIds }),
  exportExcel: (params) => api.get('/marketing-expenses/export', { params, responseType: 'blob' }),
  // Bulk import functions
  downloadTemplate: (year) => api.get('/marketing-expenses/template', {
    params: { year },
    responseType: 'blob'
  }),
  bulkImport: (formData) => api.post('/marketing-expenses/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// ============================================
// MARKETING QUOTE API
// ============================================
export const marketingQuoteAPI = {
  getAll: (params) => api.get('/marketing-quotes', { params }),
  getByBudget: (budgetId) => api.get(`/marketing-quotes/by-budget/${budgetId}`),
  getByContractor: (contractorId) => api.get(`/marketing-quotes/by-contractor/${contractorId}`),
  summaryByContractor: (params) => api.get('/marketing-quotes/summary-by-contractor', { params }),
  getUnlinkedExpenses: (budgetId) => api.get(`/marketing-quotes/unlinked-expenses/${budgetId}`),
  getById: (id) => api.get(`/marketing-quotes/${id}`),
  create: (data) => api.post('/marketing-quotes', data),
  update: (id, data) => api.put(`/marketing-quotes/${id}`, data),
  updateStatus: (id, status) => api.put(`/marketing-quotes/${id}/status`, { status }),
  delete: (id) => api.delete(`/marketing-quotes/${id}`),
  // Quote items
  getItems: (quoteId) => api.get(`/marketing-quotes/${quoteId}/items`),
  addItem: (quoteId, data) => api.post(`/marketing-quotes/${quoteId}/items`, data),
  updateItem: (quoteId, itemId, data) => api.put(`/marketing-quotes/${quoteId}/items/${itemId}`, data),
  deleteItem: (quoteId, itemId) => api.delete(`/marketing-quotes/${quoteId}/items/${itemId}`),
  // Quote-expense linking
  getExpenses: (quoteId) => api.get(`/marketing-quotes/${quoteId}/expenses`),
  linkExpenses: (quoteId, expenseIds) => api.post(`/marketing-quotes/${quoteId}/expenses`, { expenseIds }),
  unlinkExpense: (quoteId, expenseId) => api.delete(`/marketing-quotes/${quoteId}/expenses/${expenseId}`),
  exportPdf: (id) => api.get(`/marketing-quotes/${id}/export-pdf`, { responseType: 'blob' })
};

// ============================================
// MARKETING REPORT API
// ============================================
export const marketingReportAPI = {
  getMonthlyReport: (params) => api.get('/marketing-reports/monthly', { params }),
  getCategoryReport: (params) => api.get('/marketing-reports/category', { params }),
  getBudgetSummaryReport: (params) => api.get('/marketing-reports/budget-summary', { params }),
  getAAndPReport: (params) => api.get('/marketing-reports/a-and-p', { params }),
  getSalesReport: (params) => api.get('/marketing-reports/sales', { params }),
  getQuarterlyReport: (params) => api.get('/marketing-reports/quarterly', { params }),
  getExpensesDetailReport: (params) => api.get('/marketing-reports/expenses-detail', { params }),

  // Excel exports
  exportExpenses: (params) => api.get('/marketing-reports/export/expenses', { params, responseType: 'blob' }),
  exportSalesMonthly: (params) => api.get('/marketing-reports/export/sales-monthly', { params, responseType: 'blob' }),
  exportSalesQuarterly: (params) => api.get('/marketing-reports/export/sales-quarterly', { params, responseType: 'blob' }),
  exportBudgetSummary: (params) => api.get('/marketing-reports/export/budget-summary', { params, responseType: 'blob' }),
  exportMonthlyCategory: (params) => api.get('/marketing-reports/export/monthly-category', { params, responseType: 'blob' }),
  exportQuarterlyCategory: (params) => api.get('/marketing-reports/export/quarterly-category', { params, responseType: 'blob' }),
  exportMonthlyAAndP: (params) => api.get('/marketing-reports/export/monthly-aandp', { params, responseType: 'blob' }),
  exportQuarterlyAAndP: (params) => api.get('/marketing-reports/export/quarterly-aandp', { params, responseType: 'blob' })
};

// ============================================
// INVOICE API
// ============================================
export const invoiceAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  linkExpenses: (id, expenseIds) => api.post(`/invoices/${id}/link-expenses`, { expenseIds }),
  unlinkExpense: (id, expenseId) => api.delete(`/invoices/${id}/unlink-expense/${expenseId}`),
  getAvailableExpenses: (budgetId, params) => api.get(`/invoices/available-expenses/${budgetId}`, { params }),
  recalculateTotals: (id) => api.post(`/invoices/${id}/recalculate`),
  summaryByContractor: (params) => api.get('/invoices/summary-by-contractor', { params }),
  exportPdf: (id) => api.get(`/invoices/${id}/export-pdf`, { responseType: 'blob' })
};

// ============================================
// SALES REPORT API
// ============================================
// Report email bundles — named sets of independent brands whose reports are
// drafted into ONE email (managed from the Sales Reporting page).
export const reportEmailBundleAPI = {
  getAll:  ()          => api.get('/report-email-bundles'),
  create:  (data)      => api.post('/report-email-bundles', data),
  update:  (id, data)  => api.put(`/report-email-bundles/${id}`, data),
  remove:  (id)        => api.delete(`/report-email-bundles/${id}`),
};

export const salesReportAPI = {
  getConfig:       (params)        => api.get('/sales-report/config', { params }),
  // Step 7 — period-range dropdowns. Returns every (year, month) that has data.
  getAvailablePeriods: ()           => api.get('/sales-report/available-periods'),
  run:             (data)          => api.post('/sales-report/run', data),
  getStatus:       (jobId)         => api.get(`/sales-report/status/${jobId}`),
  getRuns:         (opts = {})     => api.get('/sales-report/runs', { params: opts }),
  // Monthly report status for one data month ({ year, month }); admin and reporting_manager only.
  getCoverage:     (params)        => api.get('/sales-report/coverage', { params }),
  // POST (in the body) rather than GET ?key= — a legacy absolute Windows path
  // contains backslashes and spaces that are fragile to encode through the dev
  // proxy / browsers as a query string. The body avoids all of that.
  //
  // `ref` is an object-storage key for anything generated after the storage
  // cutover, and an absolute server path for anything before it. The endpoint
  // maps either onto the same file.
  download:        (ref)           => api.post('/sales-report/download',
    { key: ref },
    { responseType: 'blob' }),
  regenerateEmail: (jobId)         => api.post(`/sales-report/email/${jobId}`),

  // ── Step 4 — amendment, approval, manual-send tracking, history ────────
  // outputId: the _id of the outputPaths row the file replaces; may be omitted
  // when the email attaches a single file.
  uploadAmendedReport: (jobId, file, outputId) => {
    const fd = new FormData();
    if (outputId) fd.append('outputId', outputId);
    fd.append('file', file);
    return api.post(`/sales-report/${jobId}/amended-report`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadAmendedEmail: (jobId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/sales-report/${jobId}/amended-email`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  approve:    (jobId, mode) => api.post(`/sales-report/${jobId}/approve`, { mode }),
  markSent:   (jobId)       => api.post(`/sales-report/${jobId}/mark-sent`),
  cancel:     (jobId)       => api.post(`/sales-report/${jobId}/cancel`),
  getHistory: (jobId)       => api.get(`/sales-report/${jobId}/history`),
  // Remove an uploaded amended file — kind ∈ {'report','email'}. outputId names
  // the row of a per-file amended Excel; without it, 'report' is the run-level one.
  deleteAmended: (jobId, kind, outputId) => api.delete(`/sales-report/${jobId}/amended/${kind}`,
    outputId ? { params: { outputId } } : undefined),
  // In-app view/edit of the email draft (amended takes precedence over generated).
  getEmailDraft:  (jobId)        => api.get(`/sales-report/${jobId}/email-draft`),
  saveEmailDraft: (jobId, body)  => api.put(`/sales-report/${jobId}/email-draft`, body),
  // Debug — bypass EOB cron and dispatch a single run immediately.
  // Body { force: true } allows re-send from a terminal state.
  sendNow:    (jobId, opts = {}) => api.post(`/sales-report/${jobId}/send-now`, opts),
  // Hard delete — removes the run document AND every file the run produced.
  // Admin only (backend gates with authorize('admin')). Intended for test-data
  // cleanup; use cancel() instead if the run must be preserved for audit.
  deleteRun:  (jobId)            => api.delete(`/sales-report/${jobId}`),
};

// Wording of the Sales Report email drafts: the standard wording (target 'default')
// and the overrides per email bundle, Reporting Group, brand and supplier.
// docs/specs/sales-reporting/EMAIL_TEMPLATES.md.
export const salesReportEmailTemplateAPI = {
  list:         ()                      => api.get('/sales-report-email-templates'),
  get:          (scope, target)         => api.get(`/sales-report-email-templates/${scope}/${target}`),
  saveDraft:    (scope, target, body)   => api.put(`/sales-report-email-templates/${scope}/${target}/draft`, body),
  discardDraft: (scope, target, expectedStagedAt) => api.delete(
    `/sales-report-email-templates/${scope}/${target}/draft`,
    { data: { expectedStagedAt } },
  ),
  publish:      (scope, target, expectedStagedAt) => api.post(
    `/sales-report-email-templates/${scope}/${target}/publish`,
    { expectedStagedAt },
  ),
  preview:      (body)                  => api.post('/sales-report-email-templates/preview', body),
  // The preview as a .eml download (message/rfc822), without attachments.
  previewEml:   (body)                  => api.post('/sales-report-email-templates/preview', body, {
    params: { format: 'eml' },
    responseType: 'blob',
  }),
};

// CAPEX (capital fixtures) API
export const capexAPI = {
  getAll:  (params) => api.get('/capex', { params }),
  getById: (id)     => api.get(`/capex/${id}`),
  create:  (data)   => api.post('/capex', data),
  update:  (id, d)  => api.put(`/capex/${id}`, d),
  delete:  (id)     => api.delete(`/capex/${id}`),
  // Auto-fill vendor + currency when a brand is picked (K column, BR-1).
  // Returns { vendor, supplierId, currencyCode }.
  vendorForBrand: (brandId) => api.get(`/capex/vendor-for-brand/${brandId}`),
  // Excel: cascading-dropdown template, bulk import, formula-parity export.
  downloadTemplate: () => api.get('/capex/template', { responseType: 'blob' }),
  export: (params) => api.get('/capex/export', { params, responseType: 'blob' }),
  // Amortization roll-up (BIB share by calendar year / brand / supplier, in BDT).
  summary: (params) => api.get('/capex/summary', { params }),
  importPreview: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/capex/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  import: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/capex/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // CAPEX expense allow-list — the curated Expenses usable on CAPEX lines.
  listExpenses: (params) => api.get('/capex/expenses', { params }),
  availableExpenses: (params) => api.get('/capex/expenses/available', { params }),
  addExpenses: (expenseIds) => api.post('/capex/expenses', { expenseIds }),
  removeExpense: (id) => api.delete(`/capex/expenses/${id}`),
};

// ============================================
// MARKETING ANALYTICS DASHBOARD API
// ============================================
// Thin composition layer for the native Marketing Analytics dashboard
// (frontend/src/pages/marketing/MarketingAnalytics.js). It does NOT add any new
// backend routes — it groups the already-existing endpoints the five reports
// draw from (product-sales, marketing-reports, outpost-sales,
// purchase-bp-dashboard, currencies) behind one namespace so the page has a
// single, self-documenting surface to call. All money is returned in BDT;
// currency display conversion happens client-side (see utils/currencyConvert.js).
// Lean field projections for the dashboard's list pulls. The GET endpoints
// accept an opt-in `?fields=` param and .select() only these columns, cutting
// the product-sales payload ~70% (each row drops the unused inventory/ytd/
// pricing/analysis blocks + timestamps). Kept here so the tabs share one list.
export const MKA_PRODUCT_SALES_FIELDS = 'brand year month itemCode itemName itemGroup upcId sales'.split(' ').join(',');
// Outpost tab reads customerName (not customerCode) and salesValue (not the unit
// mrp), so those two columns are omitted — the tab never touches them.
export const MKA_OUTPOST_FIELDS = 'year month customerName itemCode itemName brand qty salesValue focus activity'.split(' ').join(',');

export const marketingAnalyticsAPI = {
  // ── Sell-In / Sell-Out (GA product-sales grain) ─────────────────────────
  // sell-in  = sales.qtySoldWhsValue, sell-out = sales.qtySoldMrpValue.
  // Callers pass `fields: MKA_PRODUCT_SALES_FIELDS` for the lean projection.
  getBrands: () => api.get('/product-sales/brands'),
  getProductSales: (params) => api.get('/product-sales', { params }),
  // Server-side Sell-In/Sell-Out aggregate: returns { summary, byMonth, byBrand,
  // byGroup, items } grouped on the DB so the charts/pivot cover the WHOLE set
  // (no client-side 20k-row cap). Same scope params as getProductSales
  // (year, brandId, supplierId, brandSegments). See
  // productSalesController.getSellAggregate.
  getSellAggregate: (params) => api.get('/product-sales/sell-aggregate', { params }),
  getBrandSummary: (brandId, params) =>
    api.get(`/product-sales/brands/${brandId}/summary`, { params }),
  getBrandYoy: (brandId, year, month) =>
    api.get(`/product-sales/brands/${brandId}/yoy-report`, { params: { year, month } }),

  // ── A&P Analysis (marketing-reports; needs a resolvable budget) ─────────
  // Params priority: budgetId > brandId > supplierId; year → fiscalYear.
  // brandSegments narrows the budgets. Sent without a budget, brand or
  // supplier, a-and-p, budget-summary and expenses-detail answer the
  // calendar-year segment view instead. budget-summary and a-and-p mark the
  // months up to `through` (YYYY-MM; default the last complete month) as to date.
  getAAndP: (params) => api.get('/marketing-reports/a-and-p', { params }),
  getBudgetSummary: (params) => api.get('/marketing-reports/budget-summary', { params }),
  getMonthlyMarketing: (params) => api.get('/marketing-reports/monthly', { params }),
  getMarketingCategory: (params) => api.get('/marketing-reports/category', { params }),
  // A&P expense lines (drives the A&P pivot + raw table); carry atlBtl,
  // category, subcategory per line. Pass currency=BDT to keep money in BDT.
  getExpenseLines: (params) => api.get('/marketing-reports/expenses-detail', { params }),

  // ── Outpost Analysis ────────────────────────────────────────────────────
  getOutpostSales: (params) => api.get('/outpost-sales', { params }),
  getOutpostYears: () => api.get('/outpost-sales/years'),

  // ── Brand Distribution (purchase-bp-dashboard; returns BARE objects) ────
  getDistributionFilters: () => api.get('/purchase-bp-dashboard/filters'),
  getDistribution: (params) => api.get('/purchase-bp-dashboard/distribution', { params }),
  getDistributionTable: (params) =>
    api.get('/purchase-bp-dashboard/distribution-table', { params }),

  // ── Currency master (seed rates for the client-side converter) ──────────
  getActiveCurrencies: () => api.get('/currencies/active'),
};


// ============================================
// API KEYS (admin) — credentials for the public extract API
// ============================================
// The plaintext key is returned ONLY by create(); there is deliberately no
// "reveal" call, because the server stores a hash and cannot reproduce it.
export const apiKeyAPI = {
  getAll: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  revoke: (id) => api.post(`/api-keys/${id}/revoke`)
};

// Activations (Marketing > Activation + Master Data > Marketing Processes)
export {
  activationAPI,
  activationProcessAPI,
  blockingDateAPI,
  activationVenueAPI
} from './activations/api';
